import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { CAT } from "@/lib/ratings";
import api, { formatApiError } from "@/lib/api";

const small = "rounded-full glass px-3 py-1 text-[11px] font-bold";

export default function AdminRatings() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/ratings/admin/overview").then(({ data }) => setD(data)).catch(() => setD(false)), []);
  useEffect(() => { load(); }, [load]);

  const act = async (url, extra = {}) => {
    const reason = window.prompt("Reason (stored in the audit log):");
    if (!reason) return;
    try { await api.post(url, { reason, ...extra }); toast.success("Done — audited"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (d === null) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary-c" /></div>;
  if (d === false) return <p className="text-sm">Could not load ratings admin.</p>;
  const Sec = ({ title, children, testid }) => (
    <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid={testid}>
      <div className="font-display font-bold mb-3">{title}</div>{children}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-ratings">
      <Sec title={`Reports (${d.reports.filter((r) => r.status === "open").length} open)`} testid="admin-rating-reports">
        {d.reports.length === 0 ? <p className="text-xs" style={{ color: "var(--text-2)" }}>No reports.</p> : d.reports.map((r) => (
          <div key={r.report_id} className="py-2 flex items-center gap-2 text-sm border-b last:border-0" style={{ borderColor: "var(--border)" }}>
            <span className="text-[10px] mono uppercase glass rounded-full px-2">{r.target_type}</span><span className="mono text-xs">{r.target_id}</span>
            <span className="flex-1 truncate" style={{ color: "var(--text-2)" }}>{r.reason}</span>
            {r.status === "open" ? <button data-testid={`resolve-${r.report_id}`} className={small} onClick={() => act(`/ratings/admin/reports/${r.report_id}/resolve`)}>Resolve</button> : <span className="text-[11px]" style={{ color: "#22c55e" }}>resolved</span>}
          </div>
        ))}
      </Sec>
      <Sec title="Flagged / suspended links" testid="admin-flagged-links">
        {d.flagged_links.length === 0 ? <p className="text-xs" style={{ color: "var(--text-2)" }}>None.</p> : d.flagged_links.map((l) => (
          <div key={l.link_id} className="py-2 flex items-center gap-2 text-sm">
            <span className="font-semibold">{l.player_name}</span><span className="mono text-xs">{l.link_id}</span><span className="text-[10px] mono uppercase">{l.status}</span>
            <span className="ml-auto flex gap-1"><button className={small} onClick={() => act(`/ratings/admin/links/${l.link_id}/reinstate`)}>Reinstate</button><button className={small} onClick={() => act(`/ratings/admin/links/${l.link_id}/revoke`)}>Revoke</button></span>
          </div>
        ))}
      </Sec>
      <Sec title="Recent ratings" testid="admin-recent-ratings">
        {d.ratings.length === 0 ? <p className="text-xs" style={{ color: "var(--text-2)" }}>No ratings yet.</p> : d.ratings.map((r) => (
          <div key={r.rating_id} className="py-2 flex items-center gap-2 text-sm flex-wrap border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`admin-rating-${r.rating_id}`}>
            <span className="font-semibold">{r.rater_name}</span><span style={{ color: "var(--text-2)" }}>→</span><span className="font-semibold">{r.player_name}</span>
            <span className="text-[10px] mono uppercase glass rounded-full px-2">{CAT[r.category].short}</span><span className="mono">{Number(r.scores.overall).toFixed(2)}</span>
            {r.suspicious && <span className="text-[10px] mono uppercase" style={{ color: "#f59e0b" }}>suspicious</span>}
            <span className="text-[10px] mono uppercase" style={{ color: r.status === "active" ? "#22c55e" : "#ef4444" }}>{r.status}</span>
            <span className="ml-auto flex gap-1">
              {r.status === "active" ? <button data-testid={`void-${r.rating_id}`} className={small} onClick={() => act(`/ratings/admin/ratings/${r.rating_id}/void`)}>Void</button>
                : <button data-testid={`restore-${r.rating_id}`} className={small} onClick={() => act(`/ratings/admin/ratings/${r.rating_id}/restore`)}>Restore</button>}
              <button className={small} onClick={() => act(`/ratings/admin/users/${r.rater_id}/rating-ban`, { banned: true })}>Ban rater</button>
            </span>
          </div>
        ))}
      </Sec>
      <Sec title="Audit log" testid="admin-rating-audit">
        <div className="max-h-80 overflow-y-auto text-xs mono space-y-1">
          {d.audit.map((a) => <div key={a.audit_id}><span style={{ color: "var(--text-2)" }}>{a.at.slice(0, 19)}</span> {a.action} · {a.target_type}:{a.target_id} {a.details?.reason ? `— ${a.details.reason}` : ""}</div>)}
        </div>
      </Sec>
    </div>
  );
}
